/**
 * Zustand Store - 正規化されたグラフ構造の状態管理
 * v2.0.0
 */

import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { useMemo } from 'react'
import { GraphManager } from '../core/GraphManager'
import { logger } from '../lib/logger'
import type { 
  AppState, 
  ViewState, 
  AppMode,
  SetViewModel,
  GraphViewModel,
  ImageSet,
  SetItem,
  Node,
  Connection
} from '../core/types'

// GraphManagerインスタンス（シングルトン）
const graphManager = new GraphManager()

// ============================================
// ストアの型定義
// ============================================

export interface StoreState {
  // グラフデータ（GraphManagerから同期）
  appState: AppState
  
  // UI状態
  viewState: ViewState
  
  // ローディング・エラー状態
  isLoading: boolean
  error: string | null
  
  // ============================================
  // ImageSet操作
  // ============================================
  
  createSet: (name?: string, type?: 'linear' | 'circular') => ImageSet
  deleteSet: (setId: string) => Promise<void>
  getCurrentSet: () => ImageSet | undefined
  setCurrentSet: (setId: string) => void
  setCurrentSetId: (setId: string) => void
  getAllSets: () => ImageSet[]
  
  // ============================================
  // Image & SetItem操作
  // ============================================
  
  addImageToSet: (setId: string, imageData: string, position?: number) => Promise<void>
  moveItem: (itemId: string, newPosition: number) => void
  deleteImage: (imageId: string) => Promise<void>
  getSetItems: (setId: string) => SetItem[]
  
  // ============================================
  // Node & Connection操作
  // ============================================
  
  createNode: (setId: string, imageId: string, label?: string) => Node
  createConnection: (fromNodeId: string, toNodeId: string, bidirectional?: boolean) => Connection
  connectSets: (
    fromSetId: string,
    fromImageId: string,
    toSetId: string,
    toImageId: string,
    bidirectional?: boolean
  ) => void
  
  // ============================================
  // ビューモデル取得
  // ============================================
  
  getSetViewModel: (setId: string) => SetViewModel | null
  getGraphViewModel: () => GraphViewModel
  
  // ============================================
  // 画像取得
  // ============================================
  
  getImage: (imageId: string) => Promise<string | null>
  getThumbnail: (imageId: string) => Promise<string | null>
  
  // ============================================
  // ナビゲーション
  // ============================================
  
  navigateTo: (mode: AppMode) => void
  setSelectedImage: (imageId: string | undefined) => void
  setSelectedNode: (nodeId: string | undefined) => void
  
  // ============================================
  // 再生制御
  // ============================================
  
  setPlaybackState: (isPlaying: boolean) => void
  setZoomLevel: (level: number) => void
  setCurrentIndex: (index: number) => void
  nextImage: () => void
  previousImage: () => void
  setPlaybackSpeed: (speed: number) => void
  setAutoPlay: (autoPlay: boolean) => void
  resetPlaybackState: () => void
  
  // ============================================
  // ストレージ
  // ============================================
  
  getStorageInfo: () => Promise<{
    imageCount: number
    setCount: number
    itemCount: number
    nodeCount: number
    connectionCount: number
    storageUsed: number
  }>
  clearAll: () => Promise<void>
  
  // ============================================
  // 初期化・エラー
  // ============================================
  
  initialize: () => Promise<void>
  setError: (error: string | null) => void
}

// ============================================
// Zustandストア実装
// ============================================

export const useStore = create<StoreState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // 初期状態
      appState: graphManager.getState(),
      viewState: {
        currentMode: 'home' as AppMode,
        isPlaying: false,
        zoomLevel: 1,
        graphView: {
          scale: 1,
          offsetX: 0,
          offsetY: 0
        },
        playback: {
          currentIndex: 0,
          playbackSpeed: 2000,
          autoPlay: false
        }
      },
      isLoading: false,
      error: null,
      
      // ============================================
      // ImageSet操作
      // ============================================
      
      createSet: (name, type = 'linear') => {
        const newSet = graphManager.createSet(name, type)
        // イミュータブルな部分更新
        set((state) => ({
          appState: {
            ...state.appState,
            imageSets: {
              ...state.appState.imageSets,
              [newSet.id]: newSet
            }
          },
          viewState: {
            ...state.viewState,
            selectedSetId: newSet.id
          }
        }))
        return newSet
      },
      
      deleteSet: async (setId) => {
        try {
          set({ isLoading: true })
          await new Promise(resolve => setTimeout(resolve, 0)) // 非同期処理のシミュレーション
          graphManager.deleteSet(setId)
          set((state) => ({
            appState: graphManager.getState(),
            viewState: {
              ...state.viewState,
              selectedSetId: state.viewState.selectedSetId === setId 
                ? undefined 
                : state.viewState.selectedSetId
            },
            isLoading: false
          }))
        } catch (error) {
          set({
            error: `セット削除に失敗: ${error instanceof Error ? error.message : String(error)}`,
            isLoading: false
          })
        }
      },
      
      getCurrentSet: () => {
        const state = get()
        if (!state.viewState.selectedSetId) return undefined
        return state.appState.imageSets[state.viewState.selectedSetId]
      },
      
      setCurrentSet: (setId) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            selectedSetId: setId,
            playback: {
              ...state.viewState.playback,
              currentIndex: 0  // セット変更時にインデックスをリセット
            }
          }
        }))
      },
      
      setCurrentSetId: (setId) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            selectedSetId: setId,
            playback: {
              ...state.viewState.playback,
              currentIndex: 0  // セット変更時にインデックスをリセット
            }
          }
        }))
      },
      
      getAllSets: () => {
        return Object.values(get().appState.imageSets)
      },
      
      // ============================================
      // Image & SetItem操作
      // ============================================
      
      addImageToSet: async (setId, imageData, position) => {
        try {
          set({ isLoading: true })
          const { image, item } = await graphManager.addImageToSet(setId, imageData, position)
          
          // イミュータブルな部分更新：必要な部分のみを更新
          set((state) => ({
            appState: {
              ...state.appState,
              images: {
                ...state.appState.images,
                [image.id]: image
              },
              setItems: {
                ...state.appState.setItems,
                [item.id]: item
              }
            },
            isLoading: false
          }))
        } catch (error) {
          set({
            error: `画像追加に失敗: ${error instanceof Error ? error.message : String(error)}`,
            isLoading: false
          })
        }
      },
      
      moveItem: (itemId, newPosition) => {
        graphManager.moveItem(itemId, newPosition)
        set({ appState: graphManager.getState() })
      },
      
      deleteImage: async (imageId) => {
        try {
          set({ isLoading: true })
          await graphManager.deleteImage(imageId)
          set({
            appState: graphManager.getState(),
            isLoading: false
          })
        } catch (error) {
          set({
            error: `画像削除に失敗: ${error instanceof Error ? error.message : String(error)}`,
            isLoading: false
          })
        }
      },
      
      getSetItems: (setId) => {
        return graphManager.getSetItems(setId)
      },
      
      // ============================================
      // Node & Connection操作
      // ============================================
      
      createNode: (setId, imageId, label) => {
        const node = graphManager.createNode(setId, imageId, label)
        set({ appState: graphManager.getState() })
        return node
      },
      
      createConnection: (fromNodeId, toNodeId, bidirectional = false) => {
        const connection = graphManager.createConnection(
          fromNodeId,
          toNodeId,
          'transition',
          bidirectional
        )
        set({ appState: graphManager.getState() })
        return connection
      },
      
      connectSets: (fromSetId, fromImageId, toSetId, toImageId, bidirectional = false) => {
        graphManager.connectSets(fromSetId, fromImageId, toSetId, toImageId, bidirectional)
        set({ appState: graphManager.getState() })
      },
      
      // ============================================
      // ビューモデル取得
      // ============================================
      
      getSetViewModel: (setId) => {
        return graphManager.getSetViewModel(setId)
      },
      
      getGraphViewModel: () => {
        return graphManager.getGraphViewModel()
      },
      
      // ============================================
      // 画像取得
      // ============================================
      
      getImage: async (imageId) => {
        return await graphManager.getImage(imageId)
      },
      
      getThumbnail: async (imageId) => {
        return await graphManager.getThumbnail(imageId)
      },
      
      // ============================================
      // ナビゲーション
      // ============================================
      
      navigateTo: (mode) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            currentMode: mode
          }
        }))
      },
      
      setSelectedImage: (imageId) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            selectedImageId: imageId
          }
        }))
      },
      
      setSelectedNode: (nodeId) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            selectedNodeId: nodeId
          }
        }))
      },
      
      // ============================================
      // 再生制御
      // ============================================
      
      setPlaybackState: (isPlaying) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            isPlaying
          }
        }))
      },
      
      setZoomLevel: (level) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            zoomLevel: level
          }
        }))
      },
      
      setCurrentIndex: (index) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              currentIndex: index
            }
          }
        }))
      },
      
      nextImage: () => {
        const state = get()
        const { selectedSetId } = state.viewState
        if (!selectedSetId) {
          logger.log('[Store.nextImage] No selectedSetId, returning')
          return
        }
        
        const setItems = graphManager.getSetItems(selectedSetId)
        const maxIndex = setItems.length - 1
        const currentIndex = state.viewState.playback.currentIndex
        
        logger.log('[Store.nextImage] Called - currentIndex:', currentIndex, 'maxIndex:', maxIndex)
        
        let nextIndex = currentIndex + 1
        if (nextIndex > maxIndex) {
          const imageSet = state.appState.imageSets[selectedSetId]
          if (imageSet?.type === 'circular') {
            nextIndex = 0 // Loop back to start
          } else {
            nextIndex = maxIndex // Stay at end
          }
        }
        
        logger.log('[Store.nextImage] Setting nextIndex to:', nextIndex)
        
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              currentIndex: nextIndex
            }
          }
        }))
      },
      
      previousImage: () => {
        const state = get()
        const { selectedSetId } = state.viewState
        if (!selectedSetId) {
          logger.log('[Store.previousImage] No selectedSetId, returning')
          return
        }
        
        const setItems = graphManager.getSetItems(selectedSetId)
        const maxIndex = setItems.length - 1
        const currentIndex = state.viewState.playback.currentIndex
        
        logger.log('[Store.previousImage] Called - currentIndex:', currentIndex, 'maxIndex:', maxIndex)
        
        let prevIndex = currentIndex - 1
        if (prevIndex < 0) {
          const imageSet = state.appState.imageSets[selectedSetId]
          if (imageSet?.type === 'circular') {
            prevIndex = maxIndex // Loop to end
          } else {
            prevIndex = 0 // Stay at start
          }
        }
        
        logger.log('[Store.previousImage] Setting prevIndex to:', prevIndex)
        
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              currentIndex: prevIndex
            }
          }
        }))
      },
      
      setPlaybackSpeed: (speed) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              playbackSpeed: speed
            }
          }
        }))
      },
      
      setAutoPlay: (autoPlay) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              autoPlay
            }
          }
        }))
      },
      
      resetPlaybackState: () => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            playback: {
              ...state.viewState.playback,
              currentIndex: 0,
              // Reset other playback states to defaults
              autoPlay: false
            }
          }
        }))
      },
      
      // ============================================
      // ストレージ
      // ============================================
      
      getStorageInfo: async () => {
        return await graphManager.getStorageInfo()
      },
      
      clearAll: async () => {
        try {
          set({ isLoading: true })
          await graphManager.clearAll()
          set({
            appState: graphManager.getState(),
            viewState: {
              currentMode: 'home' as AppMode,
              isPlaying: false,
              zoomLevel: 1,
              graphView: {
                scale: 1,
                offsetX: 0,
                offsetY: 0
              },
              playback: {
                currentIndex: 0,
                playbackSpeed: 2000,
                autoPlay: false
              }
            },
            isLoading: false,
            error: null
          })
        } catch (error) {
          set({
            error: `データクリアに失敗: ${error instanceof Error ? error.message : String(error)}`,
            isLoading: false
          })
        }
      },
      
      // ============================================
      // 初期化・エラー
      // ============================================
      
      initialize: async () => {
        try {
          set({ isLoading: true })
          
          // GraphManagerを初期化
          graphManager.init()
          
          // 初期化の非同期処理
          await new Promise(resolve => setTimeout(resolve, 0))
          
          // GraphManagerの状態変更を購読
          graphManager.subscribe((newState) => {
            set({ appState: newState })
          })
          
          // 初期セットがない場合は作成
          const state = graphManager.getState()
          if (Object.keys(state.imageSets).length === 0) {
            const initialSet = graphManager.createSet('初回セッション')
            set((state) => ({
              appState: graphManager.getState(),
              viewState: {
                ...state.viewState,
                selectedSetId: initialSet.id
              },
              isLoading: false
            }))
          } else {
            // 最初のセットを選択
            const firstSetId = Object.keys(state.imageSets)[0]
            set((state) => ({
              appState: graphManager.getState(),
              viewState: {
                ...state.viewState,
                selectedSetId: firstSetId
              },
              isLoading: false
            }))
          }
        } catch (error) {
          set({
            error: `初期化に失敗: ${error instanceof Error ? error.message : String(error)}`,
            isLoading: false
          })
        }
      },
      
      setError: (error) => {
        set({ error })
      }
    }))
  )
)

// ============================================
// セレクタフック（便利な取得用フック）
// ============================================

// 基本状態
export const useAppState = () => useStore((state) => state.appState)
export const useViewState = () => useStore((state) => state.viewState)
export const useIsLoading = () => useStore((state) => state.isLoading)
export const useError = () => useStore((state) => state.error)

// ImageSets
export const useAllSets = () => useStore((state) => state.appState.imageSets)
export const useCurrentSet = () => useStore((state) => {
  const selectedSetId = state.viewState.selectedSetId
  
  if (!selectedSetId) {
    logger.log('[useCurrentSet] No selectedSetId, returning undefined')
    return undefined
  }
  
  const currentSet = state.appState.imageSets[selectedSetId]
  
  if (!currentSet && selectedSetId) {
    logger.error(`[useCurrentSet] Set ${selectedSetId} not found in imageSets:`, Object.keys(state.appState.imageSets))
    // Return undefined but log the error for debugging
    return undefined
  }
  
  return currentSet
})

// Navigation
export const useCurrentMode = () => useStore((state) => state.viewState.currentMode)
export const useSelectedImage = () => useStore((state) => {
  if (!state.viewState.selectedImageId) return undefined
  return state.appState.images[state.viewState.selectedImageId]
})

// Playback
export const useIsPlaying = () => useStore((state) => state.viewState.isPlaying)
export const useZoomLevel = () => useStore((state) => state.viewState.zoomLevel)

// Graph
export const useNodeCount = () => useStore((state) => Object.keys(state.appState.nodes).length)
export const useConnectionCount = () => useStore((state) => Object.keys(state.appState.connections).length)

// Simplified Playback Selectors
export const useCurrentIndex = () => useStore((state) => state.viewState.playback.currentIndex)
export const usePlaybackState = () => useStore((state) => state.viewState.playback)
export const useSelectedSetId = () => useStore((state) => state.viewState.selectedSetId)
export const useSetItems = (setId: string) => useStore((state) => {
  if (!setId) return []
  return Object.values(state.appState.setItems)
    .filter(item => item.setId === setId)
    .sort((a, b) => a.order - b.order)
})
export const useImages = () => useStore((state) => state.appState.images)

// Playback Data Selector - with proper memoization using shallow equality
type PlaybackDataResult = {
  status: 'loading' | 'error' | 'empty' | 'success'
  currentSet: ImageSet | null
  setItems: Array<SetItem & { imageUrl: string }>
}

export const usePlaybackData = (): PlaybackDataResult => {
  const selectedSetId = useStore(state => state.viewState.selectedSetId)
  const imageSets = useStore(state => state.appState.imageSets)
  const allSetItems = useStore(state => state.appState.setItems)
  const allImages = useStore(state => state.appState.images)
  
  // Debug logging for selectedSetId
  logger.log('[usePlaybackData] selectedSetId:', selectedSetId)
  logger.log('[usePlaybackData] Available sets:', Object.keys(imageSets))
  
  // Memoize the result to prevent infinite loops
  return useMemo(() => {
    logger.log('[usePlaybackData] Computing result for selectedSetId:', selectedSetId)
    
    // No selected set
    if (!selectedSetId) {
      return {
        status: 'loading' as const,
        currentSet: null,
        setItems: []
      }
    }
    
    // Get current set
    const currentSet = imageSets[selectedSetId]
    if (!currentSet) {
      return {
        status: 'error' as const,
        currentSet: null,
        setItems: []
      }
    }
    
    // Compute items
    const setItems = Object.values(allSetItems)
      .filter(item => item.setId === selectedSetId)
      .sort((a, b) => a.order - b.order)
      .map(item => ({
        ...item,
        imageUrl: allImages[item.imageId]?.url || ''
      }))
    
    // Empty set
    if (setItems.length === 0) {
      return {
        status: 'empty' as const,
        currentSet,
        setItems: []
      }
    }
    
    // Success
    logger.log('[usePlaybackData] Success - set:', currentSet.id, 'items:', setItems.length)
    return {
      status: 'success' as const,
      currentSet,
      setItems
    }
  }, [selectedSetId, imageSets, allSetItems, allImages])
}


// Expose store to window in development mode for testing
if (import.meta.env.DEV) {
  // Create a reference to the store that can be accessed from window
  const storeApi = useStore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  ;(window as any).store = storeApi
  logger.log('[Store] Exposed store to window.store for development')
}